/* eslint-disable quotes */
/* eslint-disable operator-linebreak */
/* eslint-disable import/no-unresolved */
import { useState } from 'react';
import { Grid, Box, Typography, Stack } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import isElectron from 'is-electron';
import { useNavigate } from 'react-router-dom';
import {
  selectCart,
  selectOrder,
  selectPrinter,
  selectAuth,
} from '@/store/selectors';
import {
  useFetchPosProfileDetails,
  useDeleteDraftInvoice,
  useRefundInvoice,
} from '@/services/hooks';
import { ScrollBar } from '@/styles/global';
import { printInvoice } from '@/utils/printer';
import { openModal } from '@/store/slices/modal';
import { Passcode } from '@/components/Modals';

import {
  PaymentAndDeleteButton,
  RefundAndPrintButton,
  RefundedButton,
  OverDueButton,
} from './Buttons';
import { useFetchCompany } from '@/services/hooks/useFetchCompany';

interface InvoiceDetailsProps {
  status: any;
}

const InvoiceDetails: React.FC<InvoiceDetailsProps> = ({ status }) => {
  const [actionType, setActionType] = useState<
    'delete' | 'refund' | '' | 'info'
  >('');
  const [showPasscode, setShowPasscode] = useState(false);

  const { items } = useSelector(selectCart);
  const {
    taxes,
    discount,
    customer,
    current_invoice: invoice,
    change,
    changeGiven,
    changeSaved,
    paymentMethod,
    sub_total: subTotal,
    total,
  } = useSelector(selectOrder);
  const dispatch = useDispatch();
  const { data: profile } = useFetchPosProfileDetails();
  const { data: company } = useFetchCompany();
  const { fullName } = useSelector(selectAuth);
  const companyDetails = company?.message[0]?.data;
  const navigate = useNavigate();

  const { default: defaultPrinter } = useSelector(selectPrinter);
  const { mutate: deleteInvoice, isLoading: isDeleting } =
    useDeleteDraftInvoice();
  const { mutate: refundInvoice, isLoading: isRefunding } = useRefundInvoice();

  const restaurant = profile?.restaurant;
  const currency = profile?.currency;

  const handleSubmitPayment = () => {
    dispatch(openModal({ type: 'submitPayment' }));
  };

  const handleAddPayment = () => {
    dispatch(openModal({ type: 'addPayment' }));
  };

  const handlePasscodeModal = () => {
    setShowPasscode(!showPasscode);
  };

  const handleDeleteAction = () => {
    setActionType('delete');

    setShowPasscode(true);
  };

  const handleRefundAction = () => {
    setActionType('refund');

    setShowPasscode(true);
  };

  const handleInvoicesFinalAction = () => {
    handlePasscodeModal();
    if (actionType === 'delete') {
      deleteInvoice();
    } else {
      refundInvoice();
    }
  };

  const handlePrintInvoice = () => {
    const printerObj = {
      copyType: "Customer's copy",
      showBusinessInfo: profile?.custom_show_business_details_on_receipt,
      businessInfo: companyDetails || {},
      taxId: profile?.tax_id,
      ipAddress: defaultPrinter.ipAddress,
      port: defaultPrinter.port,
      invoice,
      items,
      customer,
      taxes,
      cashier: fullName,
      restaurant,
      netTotal: total.toString(),
      subTotal: subTotal.toString(),
      currency,
      message: profile?.print_footer,
      change,
      changeGiven,
      changeSaved,
      paymentMethod: profile?.custom_enable_payment_method_on_invoice
        ? paymentMethod
        : [],
      discount: discount.toString(),
    };

    if (isElectron()) {
      printInvoice(printerObj);
    } else {
      navigate('/generate-invoice', {
        state: {
          ...printerObj,
        },
      });
    }
  };

  // THIS RENDERS BUTTON BASED ON THE INVOICE STATUS
  const ActionButton = () => {
    let btn;

    if (status === 'Draft') {
      btn = (
        <PaymentAndDeleteButton
          handleSubmitPayment={handleSubmitPayment}
          handleDeleteInvoice={handleDeleteAction}
          isLoading={isDeleting}
        />
      );
    } else if (status === 'Paid') {
      btn = (
        <RefundAndPrintButton
          handlePrintInvoice={handlePrintInvoice}
          handleRefundInvoice={handleRefundAction}
          isLoading={isRefunding}
        />
      );
    } else if (status === 'Return') {
      btn = <RefundedButton handlePrintInvoice={handlePrintInvoice} />;
    } else if (
      status === 'Partly Paid' ||
      status === 'Overdue' ||
      status === 'Unpaid'
    ) {
      btn = (
        <OverDueButton
          handlePrintInvoice={handlePrintInvoice}
          handleAddPayment={handleAddPayment}
        />
      );
    } else {
      btn = <Box flex={2} />;
    }

    return btn;
  };
  return (
    <>
      <Passcode
        show={showPasscode}
        handleAction={handleInvoicesFinalAction}
        handleModalClose={handlePasscodeModal}
      />

      <Box mt={0}>
        <Grid container columnSpacing={4} rowSpacing={4}>
          {/* ITEMS LIST */}
          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid #D9D9D9',
                borderRadius: '20px',
                backgroundColor: '#fff',
                width: '100%',
                maxHeight: '200px',
                height: '150px',
                overflow: 'auto',
                padding: '32px 45px',
                ...ScrollBar,
              }}
            >
              {items?.map((item) => (
                <Stack
                  direction="row"
                  key={item.item_code}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mx: 'auto',
                    mb: 1,
                    borderBottom: '1px solid #EDF2F7',
                    pb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: '600',
                      fontFamily: 'Montserrat',
                      textTransform: 'capitalize',
                      flex: 1,
                      textAlign: 'left',
                      color: '#051252',
                      fontSize: '13px',
                    }}
                  >
                    {item.item_name}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontFamily: 'Montserrat',
                      color: '#718096',
                      flex: 1,
                      fontSize: '14px',
                    }}
                  >
                    {item.qty}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontFamily: 'Montserrat',
                      color: '#718096',
                      flex: 1,
                      fontSize: '14px',
                    }}
                  >
                    {profile?.currency}
                    {item.price_list_rate.toLocaleString('en-US')}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontFamily: 'Montserrat',
                      flex: 1,
                      color: '#1A202C',
                      fontSize: '14px',
                    }}
                  >
                    {profile?.currency}
                    {(item.qty * item.price_list_rate).toLocaleString('en-US')}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Grid>

          {/* ITEMS TOTAL */}
          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid #D9D9D9',
                borderRadius: '20px',
                backgroundColor: '#fff',
                width: '100%',
                maxHeight: '200px',
                height: '200px',
                overflow: 'auto',
                padding: '32px 45px',
                ...ScrollBar,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: '600',
                    fontFamily: 'Montserrat',
                  }}
                >
                  Subtotal
                </Typography>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: '600',
                    fontFamily: 'Montserrat',
                  }}
                >
                  {profile?.currency}
                  {subTotal.toLocaleString('en-US')}
                </Typography>
              </Stack>
              {taxes?.map((tax) => (
                <Stack
                  key={tax.account_head}
                  direction="row"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <Typography fontFamily="Montserrat">
                    {tax.description}
                  </Typography>
                  <Typography fontFamily="Montserrat">
                    {profile?.currency}
                    {tax.tax_amount.toLocaleString('en-US')}
                  </Typography>
                </Stack>
              ))}

              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography fontFamily="Montserrat">Discount</Typography>
                <Typography fontFamily="Montserrat">
                  {profile?.currency}
                  {discount.toLocaleString('en-US')}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: '600',
                    fontFamily: 'Montserrat',
                  }}
                >
                  Total
                </Typography>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    fontWeight: '600',
                    fontFamily: 'Montserrat',
                  }}
                >
                  {profile?.currency}
                  {total.toLocaleString('en-US')}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography fontFamily="Montserrat">Change</Typography>
                <Typography fontFamily="Montserrat">
                  {profile?.currency}
                  {change.toLocaleString('en-US')}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography fontFamily="Montserrat">Change given</Typography>
                <Typography fontFamily="Montserrat">
                  {profile?.currency}
                  {changeGiven.toLocaleString('en-US')}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography fontFamily="Montserrat">Change saved</Typography>
                <Typography fontFamily="Montserrat">
                  {profile?.currency}
                  {changeSaved.toLocaleString('en-US')}
                </Typography>
              </Stack>
            </Box>
          </Grid>

          {/* PRINT INVOICE BUTTON */}
          <Grid item xs={6} sx={{ mx: 'auto' }}>
            {ActionButton()}
            {/* <Button
            type="submit"
            variant="contained"
            sx={{ borderRadius: 2 }}
            onClick={handlePrintInvoice}
          >
            Print Invoice
          </Button>

          <Button
            type="submit"
            variant="contained"
            sx={{ borderRadius: 2, mt: 3 }}
            onClick={handleModalClose}
          >
            Done
          </Button> */}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default InvoiceDetails;
